import React, { FunctionComponent } from 'react'
import { Field, Form } from 'react-final-form'
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { genericErrorMessage, validateEmailForm } from '@lyrahealth-inc/shared-app-logic'

import { FormContainer, LyraLogo, SsoButton } from '../../atoms'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { InputFieldRFF } from '../../atoms/formElements/InputField'
import { Link } from '../../atoms/link/Link'
import { OrDivider } from '../../atoms/orDivider/OrDivider'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { SSO_BUTTON_MESSAGE_TYPE, SSO_BUTTON_TYPE } from '../../atoms/ssoButton/SsoButton'
import { IS_WEB } from '../../constants'
import { InlineErrorBanner, InputAccessory } from '../../molecules'
import { NavBar } from '../../molecules/navBar/NavBar'
import { Flex1View, StickyView } from '../../templates'
import { ThemeType, tID } from '../../utils'

export interface EmailCaptureFormValues {
  emailAddress: string
}

const Flex1Container = styled(Flex1View)<{ theme: ThemeType; isPreview?: boolean }>(({ theme, isPreview = false }) => ({
  backgroundColor: theme.colors.backgroundTransparent,
  paddingBottom: theme.spacing['16px'],
  ...(theme.breakpoints.isMinWidthTablet && {
    borderRadius: 16,
    padding: '32px 48px',
    margin: '0 auto 48px auto',
    width: 446,
    display: 'flex',
    flexDirection: 'column',
  }),
  ...(isPreview && {
    boxSizing: 'border-box',
  }),
  ...(isPreview &&
    theme.breakpoints.isMinWidthTablet && {
      width: '464px',
      padding: `${theme.spacing['48px']} 0 0 0`,
    }),
  ...(isPreview &&
    theme.breakpoints.isMobileSized && {
      paddingTop: '18px 16px 16px',
    }),
}))

const EmailFormContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.breakpoints.isMinWidthTablet
    ? 0
    : `${theme.spacing['0px']} ${theme.spacing['16px']} ${theme.spacing['16px']}`,
}))

const IllustrationContainer = styled.View({
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const FooterTextContainer = styled.View({
  width: '100%',
  alignItems: 'center',
})

const ButtonGroupContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing['8px'],
}))

const ButtonContainer = styled.View<{ theme: ThemeType; addMarginBottom?: boolean }>(({ theme, addMarginBottom }) => ({
  width: '100%',
  ...(addMarginBottom && {
    marginBottom: theme.spacing['16px'],
  }),
}))

const AlreadyHaveAccountText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  color: theme.colors.textSecondary,
}))

const RecaptchaContainer = styled.View<{ theme: ThemeType; lessMargin: boolean }>(({ theme, lessMargin }) => ({
  marginTop: lessMargin ? theme.spacing['24px'] : theme.spacing['72px'],
}))

export interface RegistrationVariantEmailCaptureFormProps {
  onSignUpButtonPress: (values: EmailCaptureFormValues) => void
  onSignInLinkPress: () => void
  onBackButtonPress?: () => void
  appleSSOAvailable?: boolean
  googleSSOAvailable?: boolean
  onAppleSignUpButtonPress?: () => void
  onGoogleSignUpButtonPress?: () => void
  isGenerateVerificationTokenLoading?: boolean
  customAppleSSOContent?: React.ReactNode
  customGoogleSSOContent?: React.ReactNode
  reCaptchaContent?: React.ReactNode
  isEmailError?: boolean
  emailErrorMessage?: MessageDescriptor
  isPreview?: boolean
  previewComponents?: React.ReactElement
}

export const RegistrationVariantEmailCaptureForm: FunctionComponent<RegistrationVariantEmailCaptureFormProps> = ({
  appleSSOAvailable,
  googleSSOAvailable,
  onAppleSignUpButtonPress = noop,
  onGoogleSignUpButtonPress = noop,
  onBackButtonPress,
  onSignInLinkPress,
  onSignUpButtonPress,
  isGenerateVerificationTokenLoading,
  customAppleSSOContent,
  customGoogleSSOContent,
  reCaptchaContent,
  isEmailError,
  emailErrorMessage,
  isPreview,
  previewComponents,
}) => {
  const name = 'RegistrationVariantEmailCaptureForm'
  const isSsoAvailable = appleSSOAvailable || googleSSOAvailable || customAppleSSOContent || customGoogleSSOContent
  const { formatMessage } = useIntl()
  const { colors, breakpoints } = useTheme()

  const illustration = <LyraLogo width={56} height={32} fillColor={colors.icons.lyraLogoPrimaryIcon.fill} />

  const body = (
    <>
      <IllustrationContainer>{illustration}</IllustrationContainer>
      <EmailFormContainer testID={tID(`${name}-formContainer`)}>
        <Form
          onSubmit={onSignUpButtonPress}
          initialValues={{ emailAddress: '' }}
          validate={(formValues) => validateEmailForm(formValues, formatMessage)}
          render={({ handleSubmit, errors, touched }) => (
            <FormContainer handleSubmit={handleSubmit}>
              {isPreview && previewComponents}
              {isEmailError && <InlineErrorBanner text={formatMessage(emailErrorMessage || genericErrorMessage)} />}
              <Field
                testID={`${name}-emailAddressField`}
                name='emailAddress'
                label={formatMessage({
                  defaultMessage: 'Personal email',
                  description: 'Label of field for the user to enter their personal email address',
                })}
                component={InputFieldRFF}
                baseInputStyle={{ marginBottom: 16 }}
                inputContainerStyle={{ borderRadius: 8 }}
                inputAccessoryViewID={name}
                returnKeyType='done'
                autoComplete='email'
                textContentType='emailAddress'
                autoCapitalize='none'
                error={touched && errors?.emailAddress}
              />
              <ButtonGroupContainer>
                <PrimaryButton
                  loading={!!isGenerateVerificationTokenLoading}
                  fullWidth
                  testID={tID(`${name}-defaultContinueButton`)}
                  text={formatMessage({
                    defaultMessage: 'Continue',
                    description: 'Button that allows user to continue',
                  })}
                  onPress={handleSubmit}
                />
                {isSsoAvailable && <OrDivider />}
                {googleSSOAvailable && (
                  <ButtonContainer addMarginBottom={appleSSOAvailable}>
                    <SsoButton
                      testId={tID('Registration-GoogleButton')}
                      type={SSO_BUTTON_TYPE.GOOGLE}
                      messageType={SSO_BUTTON_MESSAGE_TYPE.CONTINUE}
                      onPress={onGoogleSignUpButtonPress}
                    />
                  </ButtonContainer>
                )}
                {customGoogleSSOContent}
                {appleSSOAvailable && (
                  <ButtonContainer>
                    <SsoButton
                      testId={tID('Registration-AppleButton')}
                      type={SSO_BUTTON_TYPE.APPLE}
                      messageType={SSO_BUTTON_MESSAGE_TYPE.CONTINUE}
                      onPress={onAppleSignUpButtonPress}
                    />
                  </ButtonContainer>
                )}
                {customAppleSSOContent}
              </ButtonGroupContainer>
            </FormContainer>
          )}
        />
      </EmailFormContainer>
      <FooterTextContainer>
        <AlreadyHaveAccountText
          text={
            <FormattedMessage
              defaultMessage='Already have an account? <link>Log In</link>'
              description='Message on registration page prompting users to sign in with an existing account'
              values={{
                link: (text: string) => (
                  <Link testID={tID(`${name}-signInLink`)} onPress={onSignInLinkPress} text={text} underline />
                ),
              }}
            />
          }
        />
      </FooterTextContainer>
    </>
  )

  return (
    <>
      <Flex1Container testID={tID(name)} isPreview={isPreview}>
        <StickyView
          headerBackgroundColor={!IS_WEB ? colors.backgroundTransparent : undefined}
          headerComponent={
            !!onBackButtonPress && <NavBar isTransparent={!IS_WEB} showBackButton onBack={onBackButtonPress} />
          }
          safeAreaInsets={IS_WEB && breakpoints.isMobileSized ? { top: 30, bottom: 0 } : { bottom: 0 }}
        >
          {body}
          {!IS_WEB && <RecaptchaContainer lessMargin={!!appleSSOAvailable}>{reCaptchaContent}</RecaptchaContainer>}
        </StickyView>
        <InputAccessory nativeID={name} />
      </Flex1Container>
      {IS_WEB && reCaptchaContent}
    </>
  )
}
